import { PlusIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FilterRow } from "@/components/filter-builder/filter-row";
import { Button } from "@/components/ui/button";
import type { AvailableFilterField } from "@/hooks/use-available-filter-fields";
import {
  type FilterGroup,
  type FilterOperator,
  filterGroupToList,
  listToFilterGroup,
  type StructuredFilter,
} from "@/lib/filters";

export interface FilterBuilderProps {
  availableFields: AvailableFilterField[];
  value: FilterGroup;
  onChange: (group: FilterGroup) => void;
  hideAdd?: boolean;
  className?: string;
}

interface RowModel {
  id: string; // local row id for React keying
  filter: StructuredFilter;
}

// Simple helper to generate semi-stable ids without external deps
const genId = () => Math.random().toString(36).substring(2, 10);

export function FilterBuilder({
  availableFields,
  value,
  onChange,
  hideAdd = false,
  className,
}: FilterBuilderProps) {
  // ---------------------------------------------------------------------------
  // Local row state derived from `value` prop.
  // `value` is the source of truth for filter data.
  // `rows` primarily adds stable local IDs for React keys.
  // ---------------------------------------------------------------------------

  const [rows, setRows] = useState<RowModel[]>(() =>
    filterGroupToList(value).map((f) => ({ id: genId(), filter: f })),
  );

  // Synchronize `value` prop to local `rows` state.
  // This effect is responsible for translating the `value` prop (which dictates
  // the filter structure) into the local `rows` array, while attempting to
  // preserve existing `id`s for stable React keys.
  useEffect(() => {
    setRows((prevLocalRows) => {
      const filtersFromProp = filterGroupToList(value);
      return filtersFromProp.map((filter, idx) => ({
        // If a row already exists at this position in the local state, reuse its ID.
        // Otherwise, (e.g., new filter added via prop), generate a new ID.
        id: prevLocalRows[idx]?.id ?? genId(),
        filter,
      }));
    });
  }, [value]); // Depends only on `value` and stable utility functions

  // ---------------------------------------------------------------------------
  // Handlers for filter modifications - these now call `onChange` to update parent
  // ---------------------------------------------------------------------------

  const currentFilters = useMemo(() => filterGroupToList(value), [value]);

  const handleRowChange = useCallback(
    (rowId: string, updatedFilter: StructuredFilter) => {
      // Create the next list of filters based on current `rows` state,
      // but with the specific row updated.
      const updatedRows = rows.map((r) =>
        r.id === rowId ? { ...r, filter: updatedFilter } : r,
      );
      onChange(listToFilterGroup(updatedRows.map((r) => r.filter)));
    },
    [rows, onChange], // listToFilterGroup should be stable
  );

  const handleDelete = useCallback(
    (rowId: string) => {
      const updatedRows = rows.filter((r) => r.id !== rowId);
      onChange(listToFilterGroup(updatedRows.map((r) => r.filter)));
    },
    [rows, onChange], // listToFilterGroup should be stable
  );

  const handleAdd = useCallback(() => {
    let defaultOp: FilterOperator | null = null; // Initialize op as null

    if (availableFields.length > 0) {
      const firstField = availableFields[0];
      if (firstField.validOperators && firstField.validOperators.length > 0) {
        defaultOp = firstField.validOperators[0];
        // Apply more specific default operator logic based on field type if desired, similar to FilterRow
        // For now, just taking the first valid one if a field type context exists.
      } else {
        console.warn(
          `Field "${firstField.label}" has no valid operators. Cannot determine a default operator.`,
        );
      }
    } else {
      console.warn(
        "No available fields to add a filter. Cannot determine a default operator.",
      );
    }

    const newFilter: StructuredFilter = {
      field: null, // Field is initially null
      op: defaultOp, // op can be null if no default is found
      values: null, // Values are initially null
    };

    // Append the new filter and inform the parent
    onChange(listToFilterGroup([...currentFilters, newFilter]));
  }, [availableFields, currentFilters, onChange]); // listToFilterGroup, filterGroupToList should be stable

  // Compute allowed fields per row – currently returns all fields (extendable)
  const computeAvailableFieldsForRow = useCallback(
    (): AvailableFilterField[] => availableFields,
    [availableFields],
  );

  const availableFieldsForRow = useMemo(
    () => computeAvailableFieldsForRow(),
    [computeAvailableFieldsForRow],
  );

  return (
    <div className={className}>
      <div className="space-y-2">
        {rows.map((row) => (
          <FilterRow
            key={row.id}
            filter={row.filter}
            availableFields={availableFieldsForRow}
            onChange={(updated) => handleRowChange(row.id, updated)}
            onDelete={() => handleDelete(row.id)}
          />
        ))}
      </div>
      {!hideAdd && (
        <div className="mt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleAdd}
            disabled={rows.length >= availableFields.length}
          >
            <PlusIcon className="h-4 w-4 mr-1" /> Add filter
          </Button>
        </div>
      )}
    </div>
  );
}
